import { __awaiter } from "tslib";
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useField } from 'formik';
import styled from '@emotion/styled';
import { Button } from '../../button';
import { theme } from '../../../theme';
const StyledInput = styled.input `
    display: none;
`;
const DropzoneContainer = styled.div `
    height: ${({ isSmall }) => isSmall ? 'initial' : '100%'};
    min-height: ${({ isSmall }) => isSmall ? 'initial' : '200px'};;
    background-color: ${({ isDragging }) => isDragging ? theme.colors.LM_NU20 : theme.colors.LM_NU10};
    padding: ${theme.spacing.space50};
    border: 1px dashed ${theme.colors.LM_NU50T};
    border-radius: ${theme.radii.radii30};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;

    p {
        ${theme.typography.IR90}
        text-align: center;
    }

    &:hover {
        background-color: ${theme.colors.LM_NU20};
    }

    &:active {
        background-color: ${theme.colors.LM_NU30};
    }

    &:focus, &:focus-visible {
        border: 1px dashed ${theme.colors.BB80};
        outline: none;
    }

    ${({ disabled }) => disabled ? `
        opacity: 0.2;
        background-color: ${theme.colors.LM_NU10};
        border: 1px dashed ${theme.colors.LM_NU10};
        cursor: not-allowed;

        p {
            color: ${theme.colors.LM_NU70T};
        }

        &:hover,
        &:active,
        &:focus {
            background-color: ${theme.colors.LM_NU10};
        }
    ` : ''}
`;
const DragAndDropLabel = styled.p `
    color: ${theme.colors.typography.label};
    margin-top: ${theme.spacing.space40};
    margin-bottom: 0;
`;
const Label = styled.p `
    bottom: ${theme.spacing.space50};
    position: ${({ isSmall }) => isSmall ? 'initial' : 'absolute'};
    color: ${theme.colors.typography.body};
    margin: 0;
    margin-top: ${theme.spacing.space40};
`;
const ButtonContainer = styled.div `
    display: flex;
    flex-direction: column;
    align-items: center;
`;
// Helper function to check if a file type matches the accept criteria
// we need to do this manually to match the same logic used with drag/drop as the accept prop on the native file input
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#accept
const isFileTypeAccepted = (file, accept) => {
    const acceptedTypes = accept.split(',').map(type => type.trim());
    for (const type of acceptedTypes) {
        if (type.startsWith('.')) {
            // File extension case. eg: '.jpg'
            if (file.name.endsWith(type))
                return true;
        }
        else if (type.endsWith('/*')) {
            // Wildcard MIME type case. eg: 'image/*'
            const baseType = type.replace('/*', '');
            if (file.type.startsWith(baseType))
                return true;
        }
        else {
            // Exact MIME type case. eg: 'image/jpeg'
            if (file.type === type)
                return true;
        }
    }
    return false;
};
export const FileInput = forwardRef((props, ref) => {
    const { label, buttonProps, disabled, dragAndDropLabel, size } = props;
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    useImperativeHandle(ref, () => fileInputRef.current);
    // Formik connector
    const [field, meta, helpers] = useField(props.name);
    const handleClick = (e) => {
        var _a;
        e.stopPropagation();
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    }, []);
    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    }, []);
    const handleDrop = useCallback((event) => {
        var _a;
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        const files = (_a = event === null || event === void 0 ? void 0 : event.dataTransfer) === null || _a === void 0 ? void 0 : _a.files;
        if (files && files.length) {
            const filesAllValid = props.accept ? Array.from(files).every(file => isFileTypeAccepted(file, props.accept)) : true;
            // validate files against accept criteria
            if (filesAllValid) {
                void helpers.setValue(files);
            }
        }
    }, [props]);
    return (React.createElement(React.Fragment, null,
        React.createElement(DropzoneContainer, { tabIndex: 0, disabled: disabled, onClick: handleClick, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, isDragging: isDragging, isSmall: size === 'small' },
            React.createElement(StyledInput, Object.assign({ type: "file", ref: fileInputRef, "data-testid": "input", "aria-label": props['aria-label'] || label }, field, meta, props, { 
                // undefined is needed to keep formik happy
                value: undefined, onChange: (e) => __awaiter(void 0, void 0, void 0, function* () {
                    // this replaces the current value with the new files
                    yield helpers.setValue(e.currentTarget.files);
                }) })),
            React.createElement(ButtonContainer, null,
                React.createElement(Button, Object.assign({ icon: "FilePlus", text: "Choose files" }, buttonProps, { onClick: (e) => {
                        var _a;
                        (_a = buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(buttonProps, e);
                        handleClick(e);
                    }, disabled: disabled })),
                (dragAndDropLabel && size !== 'small') &&
                    React.createElement(DragAndDropLabel, null, dragAndDropLabel)),
            label &&
                React.createElement(Label, { isSmall: size === 'small' }, label))));
});
//# sourceMappingURL=index.js.map